// src/features/etf-info/components/IntradayChart.tsx
//
// Component Island: interactive Price / Volume chart with technical-indicator
// overlays. Identical to the stock-info IntradayChart -- users can:
//   - toggle the technical indicator (SMA / EMA / WMA / RSI / ADX),
//   - pick the timeframe (15min .. 1day),
//   - set the indicator period length, and
//   - choose a From / To calendar window,
// mirroring FMP's /stable/technical-indicators/{indicator} endpoint.
//
// SMA/EMA/WMA are price-scale overlays drawn on the main chart. RSI/ADX are
// bounded 0-100 oscillators, so they render in a dedicated sub-panel with a
// 0-100 axis (RSI adds 30/70 reference bands, ADX a 25 trend-strength line).
//
// FMP returns bars newest-first; Recharts plots left-to-right chronologically,
// so we reverse() a shallow copy before charting.

import { useEffect, useMemo, useState } from 'react';
import {
    Area,
    Bar,
    CartesianGrid,
    ComposedChart,
    Line,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import {
    fetchTechnicalIndicator,
    type TechnicalIndicator,
    type TechnicalIndicatorBar,
} from '../api';
import { currencyGlyph, type CurrencyCode } from '../../../shared/utils/currency';

const INDICATORS: { label: string; value: TechnicalIndicator }[] = [
    { label: 'SMA', value: 'sma' },
    { label: 'EMA', value: 'ema' },
    { label: 'WMA', value: 'wma' },
    { label: 'RSI', value: 'rsi' },
    { label: 'ADX', value: 'adx' },
];

const TIMEFRAMES = [
    { label: '15 Min', value: '15min' },
    { label: '30 Min', value: '30min' },
    { label: '1 Hour', value: '1hour' },
    { label: '4 Hour', value: '4hour' },
    { label: '1 Day', value: '1day' },
] as const;

// RSI/ADX are 0-100 oscillators; the rest overlay directly on the price scale.
const OSCILLATORS: TechnicalIndicator[] = ['rsi', 'adx'];

const INDICATOR_COLOR = '#f59e0b'; // amber overlay line

const todayIso = (): string => new Date().toISOString().slice(0, 10);

const isoDaysAgo = (days: number): string => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
};

const formatVolume = (v: number): string => {
    if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
    if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
    if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
    return `${v}`;
};

export const IntradayChart = ({
    symbol,
    currency = 'USD',
}: {
    symbol: string;
    currency?: CurrencyCode;
}) => {
    const glyph = currencyGlyph({ explicitCurrency: currency });
    const [indicator, setIndicator] = useState<TechnicalIndicator>('sma');
    const [timeframe, setTimeframe] = useState<string>('1day');
    const [periodLength, setPeriodLength] = useState<number>(20);
    const [fromDate, setFromDate] = useState<string>(isoDaysAgo(180));
    const [toDate, setToDate] = useState<string>(todayIso());

    const [data, setData] = useState<TechnicalIndicatorBar[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const isOscillator = OSCILLATORS.includes(indicator);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(null);

        fetchTechnicalIndicator(symbol, {
            indicator,
            periodLength,
            timeframe,
            from: fromDate || undefined,
            to: toDate || undefined,
        })
            .then((res) => {
                // Reverse so Recharts plots oldest -> newest (left to right).
                if (!cancelled) setData([...res].reverse());
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error(err);
                if (!cancelled) {
                    setError('Chart unavailable.');
                    setData([]);
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [symbol, indicator, timeframe, periodLength, fromDate, toDate]);

    // Tighten the price Y-domain around the closes so the line isn't a flat streak.
    const [yMin, yMax] = useMemo<[number, number | 'auto']>(() => {
        if (!data.length) return [0, 'auto'];
        const prices = data
            .map((d) => d.close)
            .filter((n): n is number => typeof n === 'number');
        if (!prices.length) return [0, 'auto'];
        return [Math.min(...prices) * 0.98, Math.max(...prices) * 1.02];
    }, [data]);

    // Intraday bars show HH:MM; daily bars show the calendar date.
    const formatXTick = (str: string) =>
        timeframe === '1day' ? str.substring(0, 10) : str.substring(5, 16);

    const toggleClass = (active: boolean) =>
        `px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
            active
                ? 'bg-blue-600 text-white shadow'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
        }`;

    const inputClass =
        'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-2 py-1.5 text-slate-700 dark:text-slate-200';

    return (
        <div className="surface-card flex flex-col h-full">
            {/* Header + indicator toggles */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                <h2 className="section-title mb-0">Price &amp; Volume</h2>

                <div className="flex flex-wrap bg-slate-100 dark:bg-slate-800 rounded-lg p-1 border border-slate-200 dark:border-slate-700">
                    {INDICATORS.map((ind) => (
                        <button
                            key={ind.value}
                            onClick={() => setIndicator(ind.value)}
                            className={toggleClass(indicator === ind.value)}
                        >
                            {ind.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Timeframe + period + date-window controls */}
            <div className="flex flex-wrap items-end gap-3 mb-6 text-xs">
                <div className="flex flex-col gap-1">
                    <label className="text-slate-500 dark:text-slate-400 font-medium">
                        Timeframe
                    </label>
                    <select
                        value={timeframe}
                        onChange={(e) => setTimeframe(e.target.value)}
                        className={inputClass}
                    >
                        {TIMEFRAMES.map((tf) => (
                            <option key={tf.value} value={tf.value}>
                                {tf.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-slate-500 dark:text-slate-400 font-medium">
                        Period
                    </label>
                    <input
                        type="number"
                        min={2}
                        max={200}
                        value={periodLength}
                        onChange={(e) =>
                            setPeriodLength(
                                Math.max(2, Math.min(200, Number(e.target.value) || 2)),
                            )
                        }
                        className={`w-20 ${inputClass}`}
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-slate-500 dark:text-slate-400 font-medium">
                        From
                    </label>
                    <input
                        type="date"
                        value={fromDate}
                        max={toDate || todayIso()}
                        onChange={(e) => setFromDate(e.target.value)}
                        className={inputClass}
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-slate-500 dark:text-slate-400 font-medium">
                        To
                    </label>
                    <input
                        type="date"
                        value={toDate}
                        min={fromDate || undefined}
                        max={todayIso()}
                        onChange={(e) => setToDate(e.target.value)}
                        className={inputClass}
                    />
                </div>
            </div>

            {/* Main price + volume chart */}
            <div className="relative h-[320px] w-full">
                {isLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm">
                        <span className="text-blue-500 font-medium animate-pulse">
                            Loading Chart…
                        </span>
                    </div>
                )}

                {!isLoading && error && (
                    <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-500 dark:text-slate-400">
                        {error}
                    </div>
                )}

                {!isLoading && !error && data.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-500 dark:text-slate-400">
                        No data available for this window.
                    </div>
                )}

                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={data}
                        margin={{ left: -10, bottom: 0, top: 10, right: -10 }}
                    >
                        <defs>
                            <linearGradient id="colorCloseEtf" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#334155"
                            opacity={0.3}
                        />
                        <XAxis
                            dataKey="date"
                            tickFormatter={formatXTick}
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            minTickGap={40}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            yAxisId="price"
                            domain={[yMin, yMax]}
                            tickFormatter={(val: number) => `${glyph}${val.toFixed(0)}`}
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                            width={56}
                        />
                        <YAxis
                            yAxisId="volume"
                            orientation="right"
                            domain={[0, (dataMax: number) => dataMax * 4]}
                            tickFormatter={formatVolume}
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                            width={46}
                        />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: '#0f172a',
                                borderColor: '#334155',
                                borderRadius: '8px',
                            }}
                            labelStyle={{ color: '#94a3b8', marginBottom: '4px' }}
                            formatter={(value: number, name: string) =>
                                name === 'Volume'
                                    ? [formatVolume(value), name]
                                    : [`${glyph}${Number(value).toFixed(2)}`, name]
                            }
                        />
                        <Bar
                            yAxisId="volume"
                            dataKey="volume"
                            name="Volume"
                            fill="#64748b"
                            opacity={0.25}
                            isAnimationActive={false}
                        />
                        <Area
                            yAxisId="price"
                            type="monotone"
                            dataKey="close"
                            name="Price"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            fill="url(#colorCloseEtf)"
                            isAnimationActive={false}
                        />
                        {!isOscillator && (
                            <Line
                                yAxisId="price"
                                type="monotone"
                                dataKey={indicator}
                                name={indicator.toUpperCase()}
                                stroke={INDICATOR_COLOR}
                                strokeWidth={2}
                                dot={false}
                                isAnimationActive={false}
                            />
                        )}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>

            {/* Oscillator sub-panel (RSI / ADX) */}
            {isOscillator && (
                <div className="relative h-[150px] w-full mt-4">
                    <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                        {indicator.toUpperCase()} ({periodLength})
                    </div>
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                            data={data}
                            margin={{ left: -10, bottom: 0, top: 6, right: -10 }}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                                stroke="#334155"
                                opacity={0.3}
                            />
                            <XAxis
                                dataKey="date"
                                tickFormatter={formatXTick}
                                tick={{ fontSize: 11, fill: '#64748b' }}
                                minTickGap={40}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                domain={[0, 100]}
                                ticks={[0, 30, 50, 70, 100]}
                                tick={{ fontSize: 11, fill: '#64748b' }}
                                axisLine={false}
                                tickLine={false}
                                width={56}
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#0f172a',
                                    borderColor: '#334155',
                                    borderRadius: '8px',
                                }}
                                labelStyle={{ color: '#94a3b8', marginBottom: '4px' }}
                                formatter={(value: number) => [
                                    Number(value).toFixed(2),
                                    indicator.toUpperCase(),
                                ]}
                            />
                            {indicator === 'rsi' && (
                                <>
                                    <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="4 4" />
                                    <ReferenceLine y={30} stroke="#22c55e" strokeDasharray="4 4" />
                                </>
                            )}
                            {indicator === 'adx' && (
                                <ReferenceLine y={25} stroke="#94a3b8" strokeDasharray="4 4" />
                            )}
                            <Line
                                type="monotone"
                                dataKey={indicator}
                                name={indicator.toUpperCase()}
                                stroke={INDICATOR_COLOR}
                                strokeWidth={2}
                                dot={false}
                                isAnimationActive={false}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};

export default IntradayChart;
